import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { authService } from "../../services/authService";
import Icon from "../../components/Common/Icon";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();

  const [info, setInfo] = useState(null); // { email, valid }
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    authService
      .getPasswordReset(token)
      .then((res) => setInfo(res.data.data))
      .catch(() => setInfo({ valid: false }))
      .finally(() => setChecking(false));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Şifreler eşleşmiyor");
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword({ token, password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error ?? "Şifre güncellenemedi.");
    } finally {
      setLoading(false);
    }
  };

  const invalid = !token || (info && !info.valid);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 to-secondary/10 p-4">
      <div className="w-full max-w-md bg-surface-card rounded-3xl shadow-modal p-8">
        {checking ? (
          <p className="text-center text-on-surface-variant">Bağlantı kontrol ediliyor…</p>
        ) : done ? (
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-success/10 text-success flex items-center justify-center mx-auto mb-4">
              <Icon name="check_circle" className="!text-[28px]" />
            </div>
            <h1 className="text-xl font-bold text-on-surface">Şifreniz Güncellendi</h1>
            <p className="text-on-surface-variant text-sm mt-1">
              Yeni şifrenizle giriş yapabilirsiniz.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="mt-6 w-full bg-primary text-white rounded-xl py-3 font-semibold hover:opacity-90 transition"
            >
              Giriş Yap
            </button>
          </div>
        ) : invalid ? (
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-error/10 text-error flex items-center justify-center mx-auto mb-4">
              <Icon name="link_off" className="!text-[28px]" />
            </div>
            <h1 className="text-xl font-bold text-on-surface">Geçersiz veya Süresi Dolmuş Bağlantı</h1>
            <p className="text-on-surface-variant text-sm mt-1">
              Bağlantı geçersiz ya da 2 saatlik süresi dolmuş. Lütfen yeni bir
              sıfırlama bağlantısı isteyin.
            </p>
            <button
              onClick={() => navigate("/forgot-password")}
              className="mt-6 text-primary font-semibold hover:underline"
            >
              Yeni bağlantı iste
            </button>
          </div>
        ) : (
          <>
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4">
              <Icon name="lock_reset" className="!text-[28px]" />
            </div>
            <h1 className="text-2xl font-bold text-on-surface">Yeni Şifre Belirle</h1>
            <p className="text-on-surface-variant text-sm mt-1">
              <strong>{info.email}</strong> hesabı için yeni bir şifre girin.
            </p>

            {error && (
              <div className="mt-4 flex items-center gap-2 text-sm text-error bg-error/10 rounded-xl px-4 py-3">
                <Icon name="error" className="!text-[18px]" /> {error}
              </div>
            )}

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <PwdField label="Yeni Şifre" value={password} onChange={setPassword} />
              <PwdField label="Yeni Şifre (tekrar)" value={confirm} onChange={setConfirm} />
              <p className="text-xs text-on-surface-variant">
                En az 8 karakter, 1 büyük harf ve 1 rakam içermeli.
              </p>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white rounded-xl py-3 font-semibold hover:opacity-90 transition active:scale-[0.99] disabled:opacity-50"
              >
                {loading ? "Kaydediliyor…" : "Şifreyi Güncelle"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function PwdField({ label, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5 text-on-surface-variant">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          required
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-outline-variant rounded-xl pl-3.5 pr-11 py-2.5 focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition bg-white"
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-3.5 top-3 text-on-surface-variant hover:text-primary transition"
        >
          <Icon name={show ? "visibility_off" : "visibility"} className="!text-[20px]" />
        </button>
      </div>
    </div>
  );
}
